import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Users, Truck, ShieldCheck, AlertTriangle, Check, CheckCheck, Pencil, Sparkles, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Header, Empty } from "@/pages/Vehicles";

const fieldLabels = {
  name: "driver name", licence_number: "licence number", licence_expiry: "licence expiry",
  date_of_birth: "date of birth", registration: "registration", mot_due: "MOT expiry",
  cpc_expiry: "CPC expiry", tacho_card_expiry: "tacho card expiry",
};
const humanFields = (arr) => (arr || []).map((f) => fieldLabels[f] || f.replace(/_/g, " ")).join(", ");

export default function ReviewInbox() {
  const [data, setData] = useState({ drivers: [], vehicles: [], insurance: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/review-inbox");
      setData(res.data);
      window.dispatchEvent(new Event("review-inbox-updated"));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const confirmDriver = async (id) => {
    try { await api.post(`/drivers/${id}/confirm-review`); toast.success("Driver confirmed"); load(); }
    catch { toast.error("Could not confirm"); }
  };
  const confirmVehicle = async (id) => {
    try { await api.post(`/vehicles/${id}/confirm-review`); toast.success("Vehicle confirmed"); load(); }
    catch { toast.error("Could not confirm"); }
  };
  const confirmInsurance = async (id) => {
    try { await api.post(`/insurance/${id}/confirm-review`); toast.success("Policy confirmed"); load(); }
    catch { toast.error("Could not confirm"); }
  };
  const confirmAll = async () => {
    if (!window.confirm(`Mark all ${data.count} record${data.count === 1 ? "" : "s"} as checked?`)) return;
    setConfirmingAll(true);
    try { const r = await api.post("/review-inbox/confirm-all"); toast.success(`${r.data.confirmed} record${r.data.confirmed === 1 ? "" : "s"} marked as checked`); load(); }
    catch { toast.error("Could not confirm all"); }
    finally { setConfirmingAll(false); }
  };

  return (
    <div data-testid="review-inbox-page">
      <Header title="Review inbox" subtitle="Records the AI created from scanned documents — confirm the details or open them to edit" />

      {loading ? null : data.count === 0 ? (
        <Empty icon={ClipboardCheck} text="Nothing to review. Anything the AI scans that needs a check will appear here." />
      ) : (
        <div className="space-y-8">
          <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm text-blue-900" data-testid="review-inbox-summary">
            <span className="flex items-center gap-2"><Sparkles size={16} /> <span className="font-semibold">{data.count}</span> record{data.count === 1 ? "" : "s"} waiting to be confirmed. Fields the AI wasn't sure about are highlighted below.</span>
            <Button data-testid="review-confirm-all" size="sm" onClick={confirmAll} disabled={confirmingAll} className="rounded-md gap-1.5 bg-slate-900 hover:bg-slate-800"><CheckCheck size={14} /> Mark all checked</Button>
          </div>

          {data.drivers.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500 mb-3"><Users size={15} /> Drivers ({data.drivers.length})</h2>
              <div className="space-y-2">
                {data.drivers.map((d) => (
                  <div key={d.id} data-testid="review-driver-row" className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-bold text-slate-900">{d.name}</p>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><AlertTriangle size={10} /> Needs checking</span>
                        {d.scan_source === "driver" && <span data-testid="review-driver-submitted" className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"><Smartphone size={10} /> Submitted by driver</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {d.licence_number || "No licence no."}{d.licence_expiry ? ` · expires ${d.licence_expiry}` : ""}
                      </p>
                      {d.review_fields?.length > 0 && (
                        <p className="text-xs text-amber-700 mt-1">AI unsure about: {humanFields(d.review_fields)}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="review-open-driver" variant="outline" size="sm" onClick={() => navigate("/drivers")} className="rounded-md gap-1.5 border-slate-300"><Pencil size={14} /> Open</Button>
                      <Button data-testid="review-confirm-driver" size="sm" onClick={() => confirmDriver(d.id)} className="rounded-md gap-1.5 bg-slate-900 hover:bg-slate-800"><Check size={14} /> Mark checked</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.vehicles.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500 mb-3"><Truck size={15} /> Vehicles ({data.vehicles.length})</h2>
              <div className="space-y-2">
                {data.vehicles.map((v) => (
                  <div key={v.id} data-testid="review-vehicle-row" className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-bold text-slate-900">{v.registration}</p>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><AlertTriangle size={10} /> Needs checking</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {[v.make, v.model].filter(Boolean).join(" ") || "Make/model not set"}{v.mot_due ? ` · MOT ${v.mot_due}` : ""}
                      </p>
                      {v.review_fields?.length > 0 && (
                        <p className="text-xs text-amber-700 mt-1">AI unsure about: {humanFields(v.review_fields)}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="review-open-vehicle" variant="outline" size="sm" onClick={() => navigate("/vehicles")} className="rounded-md gap-1.5 border-slate-300"><Pencil size={14} /> Open</Button>
                      <Button data-testid="review-confirm-vehicle" size="sm" onClick={() => confirmVehicle(v.id)} className="rounded-md gap-1.5 bg-slate-900 hover:bg-slate-800"><Check size={14} /> Mark checked</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.insurance?.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500 mb-3"><ShieldCheck size={15} /> Insurance ({data.insurance.length})</h2>
              <div className="space-y-2">
                {data.insurance.map((p) => (
                  <div key={p.id} data-testid="review-insurance-row" className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-bold text-slate-900">{p.insurer || "Insurer TBC"}</p>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.policy_type}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><AlertTriangle size={10} /> Needs checking</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {p.policy_number || "No policy no."}{p.expiry_date ? ` · expires ${p.expiry_date}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="review-open-insurance" variant="outline" size="sm" onClick={() => navigate("/office?tab=insurance")} className="rounded-md gap-1.5 border-slate-300"><Pencil size={14} /> Open</Button>
                      <Button data-testid="review-confirm-insurance" size="sm" onClick={() => confirmInsurance(p.id)} className="rounded-md gap-1.5 bg-slate-900 hover:bg-slate-800"><Check size={14} /> Mark checked</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
